import React from "react";
import { Box, Group, Stack, Text } from "@mantine/core";
import { StaffAvatar } from "../StaffAvatar";
import styles from "./StaffIdentity.module.css";

export type StaffIdentityLayout = "horizontal" | "vertical";

export interface StaffIdentityProps {
  firstName: string;
  lastName?: string;
  /**
   * Shown under the name ahead of the title ("Employee, Senior Stylist").
   * Omit it to show the title alone.
   */
  role?: string;
  /** Shown under the name when set (e.g. "Senior Stylist"). */
  title?: string | null;
  photoUrl?: string | null;
  /** Inactive staff get the neutral grey initials circle. */
  inactive?: boolean;
  /** Rendered beside the name (e.g. a "No calendar" badge). */
  trailing?: React.ReactNode;
  /**
   * "horizontal" (default): avatar on the left, name and title stacked on the
   * right — list rows like the Staff settings page. "vertical": avatar, name
   * and title stacked and centered — column headers like Notifications.
   */
  layout?: StaffIdentityLayout;
}

/**
 * The app's standard staff identity: avatar, name, and a line with the role
 * and/or title each surface chooses to pass. Shared so staff read the same
 * everywhere.
 */
export const StaffIdentity: React.FC<StaffIdentityProps> = ({
  firstName,
  lastName = "",
  role,
  title,
  photoUrl,
  inactive = false,
  trailing,
  layout = "horizontal",
}) => {
  const isVertical = layout === "vertical";
  const subtitle = [role, title?.trim()].filter(Boolean).join(", ");

  const avatar = (
    <StaffAvatar
      photoUrl={photoUrl}
      firstName={firstName}
      lastName={lastName}
      size={44}
      radius={44}
      className={styles.avatar}
      // Active: teal-tint initials circle via design tokens. Inactive: the
      // neutral grey Mantine variant.
      color={inactive ? "gray" : undefined}
      bg={inactive ? undefined : "var(--settings-accent-tint)"}
      c={inactive ? undefined : "var(--settings-accent-ink)"}
    />
  );

  const text = (
    <Box miw={0} ta={isVertical ? "center" : undefined}>
      <Group gap="xs" wrap="nowrap" justify={isVertical ? "center" : undefined}>
        <Text className={styles.name}>
          {firstName} {lastName}
        </Text>
        {trailing}
      </Group>
      {subtitle && <Text className={styles.subtitle}>{subtitle}</Text>}
    </Box>
  );

  return isVertical ? (
    <Stack gap={6} align="center" miw={0}>
      {avatar}
      {text}
    </Stack>
  ) : (
    <Group gap={14} wrap="nowrap" miw={0}>
      {avatar}
      {text}
    </Group>
  );
};

export default StaffIdentity;
